// =============================================================================
// ConversationsLayout.tsx — Shell do inbox live chat (F16-S16).
//
// Estrutura (filtro de status é um DROPDOWN no header do ChatList, não uma
// coluna própria — o antigo StatusSideMenu, menu lateral vertical, foi
// removido por ser código morto e por comer largura horizontal da lista):
//   Col 1 (280px, fixa):        ChatList — lista de conversas + busca + dropdown de status
//   Col 2 (flex-1):             Painel da conversa
//   Col 3 (240px, collapsible): Dados do contato
//
// Responsivo:
//   - < 768px (mobile): ChatList ocupa a tela inteira. Ao selecionar
//     conversa, exibe só o detalhe.
//   - >= 768px: layout completo.
//
// Estado hoistado aqui: apenas selectedId (conversa selecionada) e o estado
// de UI do painel de contato (aberto/fechado, inline vs overlay). statusFilter
// e useConversationCounts vivem dentro do ChatList (dropdown é parte do seu
// próprio header — sem prop-drilling).
//
// Deep-link (F29-S02): `selectedId` é sincronizado (não substituído) com o
// query param `?conversation=<id>` da própria rota `/conversas` — no mount,
// se o param existir, inicializa `selectedId` com ele; ao selecionar/fechar
// uma conversa, o param é atualizado via `replace` (não empurra histórico a
// cada clique). Isso NÃO remonta o socket nem duplica estado: é só leitura
// no mount + escrita no mesmo setter que já existia.
//
// DS: light-first, tokens sem hex hardcoded.
// =============================================================================

import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import { useSyncHistoryAlert } from '../../../hooks/useSyncHistoryAlert';
import { useConversation } from '../queries';

import { ChatList } from './ChatList';
import { ContactPanel } from './ContactPanel';
import { ConversationPanel } from './ConversationPanel';
import { SyncHistoryAlertDialog } from './SyncHistoryAlertDialog';

// ---------------------------------------------------------------------------
// Placeholder da coluna de conversa
// ---------------------------------------------------------------------------

function ConversationPlaceholder(): React.JSX.Element {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center gap-3"
      style={{ background: 'var(--bg)', color: 'var(--text-3)' }}
    >
      <span
        className="inline-flex items-center justify-center rounded-full"
        style={{
          width: 56,
          height: 56,
          background: 'var(--surface-muted)',
          color: 'var(--text-3)',
        }}
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-7 h-7"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </span>
      <p className="font-sans" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)' }}>
        Selecione uma conversa
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle button do ContactPanel
// ---------------------------------------------------------------------------

interface ContactToggleButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

function ContactToggleButton({ isOpen, onClick }: ContactToggleButtonProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isOpen ? 'Fechar painel de contato' : 'Abrir painel de contato'}
      aria-expanded={isOpen}
      style={{
        position: 'absolute',
        top: 'var(--space-3)',
        right: 'var(--space-3)',
        zIndex: 10,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 'var(--radius-sm)',
        border: isOpen ? '1px solid var(--brand-azul)' : '1px solid var(--border-subtle)',
        background: isOpen
          ? 'color-mix(in srgb, var(--brand-azul) 10%, var(--bg-elev-1))'
          : 'var(--bg-elev-1)',
        color: isOpen ? 'var(--brand-azul)' : 'var(--text-3)',
        boxShadow: 'var(--elev-1)',
        cursor: 'pointer',
        transition: 'background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease)',
      }}
    >
      <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: 14, height: 14 }}
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="6.5" />
        <path d="M8 7v4" />
        <circle cx="8" cy="5" r=".5" fill="currentColor" stroke="none" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export interface ConversationsLayoutProps {
  /**
   * F43-S13: recorta a lista (e os contadores) para um único canal — a caixa
   * pessoal do vendedor em `MeuWhatsAppInboxPage`. `undefined` preserva o
   * comportamento de hoje (`/conversas`, todos os canais no escopo do ator).
   */
  readonly fixedChannelId?: string;
  /**
   * F43-S12: quando definido, força o composer para somente leitura com este
   * motivo, em QUALQUER conversa selecionada — usado por `MeuWhatsAppInboxPage`
   * quando a sessão WAHA do vendedor não está `WORKING`. Repassado direto para
   * `ConversationPanel`; ver o comentário lá para o porquê deste caminho
   * existir ao lado (não substituindo) de `access.readOnlyReason` do backend.
   */
  readonly composerDisabledReason?: string;
}

/**
 * ConversationsLayout — shell do inbox (3 colunas: lista | conversa | contato).
 *
 * Gerencia: selectedId, contactOpen (UI do painel de contato).
 * Delega: statusFilter + contagens + busca ao próprio ChatList.
 */
export function ConversationsLayout({
  fixedChannelId,
  composerDisabledReason,
}: ConversationsLayoutProps = {}): React.JSX.Element {
  // Deep-link (F29-S02): `?conversation=<id>` inicializa a seleção no mount.
  // Lazy initializer — lido uma única vez, sem efeito adicional na primeira
  // pintura (evita flash lista→conversa).
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedIdState] = React.useState<string | null>(() =>
    searchParams.get('conversation'),
  );
  const [isMobile, setIsMobile] = React.useState(false);
  const [isLarge, setIsLarge] = React.useState(false);
  const [contactOpen, setContactOpen] = React.useState(true);

  // F50-S02: "conectado mas sem histórico" — sem custo para quem não é
  // vendedor (o GET .../waha/me responde 404 e o hook trata como "nada a
  // avisar").
  const syncHistoryAlert = useSyncHistoryAlert();

  // Seleciona/limpa a conversa E reflete na URL (`replace` — não polui o
  // histórico a cada clique; o "voltar" do navegador continua coerente).
  const selectConversation = React.useCallback(
    (id: string | null) => {
      setSelectedIdState(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id !== null) {
            next.set('conversation', id);
          } else {
            next.delete('conversation');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Id inexistente/inacessível (404/403) degrada para a lista sem quebrar —
  // cobre tanto o deep-link vindo da notificação quanto qualquer seleção
  // que aponte para uma conversa que não existe mais/está fora do escopo.
  const conversationCheck = useConversation(selectedId ?? '');
  React.useEffect(() => {
    if (selectedId !== null && conversationCheck.isError) {
      selectConversation(null);
    }
  }, [selectedId, conversationCheck.isError, selectConversation]);

  React.useEffect(() => {
    const mqMobile = window.matchMedia('(max-width: 767px)');
    const mqLarge = window.matchMedia('(min-width: 1024px)');
    setIsMobile(mqMobile.matches);
    setIsLarge(mqLarge.matches);
    setContactOpen(mqLarge.matches);
    const onMobile = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    const onLarge = (e: MediaQueryListEvent) => {
      setIsLarge(e.matches);
      setContactOpen(e.matches);
    };
    mqMobile.addEventListener('change', onMobile);
    mqLarge.addEventListener('change', onLarge);
    return () => {
      mqMobile.removeEventListener('change', onMobile);
      mqLarge.removeEventListener('change', onLarge);
    };
  }, []);

  const showList = !isMobile || selectedId === null;
  const showDetail = !isMobile || selectedId !== null;
  const showContactInline = !isMobile && selectedId !== null && contactOpen && isLarge;
  const showContactOverlay = !isMobile && selectedId !== null && contactOpen && !isLarge;

  return (
    <div
      className="flex h-full overflow-hidden"
      style={{ background: 'var(--bg)', position: 'relative' }}
    >
      {/* ── Col 1: ChatList (busca + dropdown de status no próprio header) ── */}
      {showList && (
        <aside
          className="flex flex-col h-full overflow-hidden flex-shrink-0"
          style={{
            width: isMobile ? undefined : '280px',
            minWidth: isMobile ? undefined : '280px',
            flex: isMobile ? '1 1 0' : undefined,
            borderRight: '1px solid var(--border-subtle)',
            boxShadow: 'var(--elev-1)',
            background: 'var(--bg-elev-1)',
          }}
          aria-label="Lista de conversas"
        >
          <ChatList
            selectedConversationId={selectedId}
            onSelectConversation={(id) => selectConversation(id)}
            {...(fixedChannelId !== undefined ? { fixedChannelId } : {})}
          />
        </aside>
      )}

      {/* ── Col 2: Conversa ──────────────────────────────────────────────── */}
      {showDetail && (
        <main
          className="flex-1 flex flex-col h-full overflow-hidden"
          style={{ position: 'relative', minWidth: isMobile ? undefined : 320 }}
          aria-label="Conversa"
        >
          {/* Botão voltar em mobile */}
          {isMobile && selectedId !== null && (
            <div
              className="flex items-center px-4 py-3"
              style={{
                borderBottom: '1px solid var(--border-subtle)',
                background: 'var(--bg-elev-1)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <button
                type="button"
                onClick={() => selectConversation(null)}
                className="flex items-center gap-2 font-sans font-medium transition-opacity duration-fast hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-azul rounded-xs"
                style={{ fontSize: 'var(--text-sm)', color: 'var(--brand-azul)' }}
                aria-label="Voltar para a lista"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path d="M10 13L5 8l5-5" />
                </svg>
                Voltar
              </button>
            </div>
          )}

          {!isMobile && selectedId !== null && (
            <ContactToggleButton
              isOpen={contactOpen}
              onClick={() => setContactOpen((prev) => !prev)}
            />
          )}

          {selectedId !== null ? (
            <ConversationPanel
              conversationId={selectedId}
              onOpenContact={() => setContactOpen(true)}
              {...(composerDisabledReason !== undefined ? { composerDisabledReason } : {})}
            />
          ) : (
            <ConversationPlaceholder />
          )}
        </main>
      )}

      {/* Col 3: Contato inline (>= 1024px) */}
      {showContactInline && (
        <aside
          className="flex-shrink-0 h-full overflow-y-auto"
          style={{
            width: '240px',
            minWidth: '240px',
            borderLeft: '1px solid var(--border-subtle)',
            background: 'var(--bg-elev-1)',
          }}
          aria-label="Dados do contato"
        >
          <ContactPanel conversationId={selectedId!} />
        </aside>
      )}

      {/* Col 3: Contato overlay (768-1023px) */}
      {showContactOverlay && (
        <>
          <div
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, zIndex: 20, background: 'rgba(0,0,0,0.18)' }}
            onClick={() => setContactOpen(false)}
          />
          <aside
            className="flex-shrink-0 h-full overflow-y-auto"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 21,
              width: '260px',
              borderLeft: '1px solid var(--border-subtle)',
              background: 'var(--bg-elev-1)',
              boxShadow: 'var(--elev-4)',
            }}
            aria-label="Dados do contato"
          >
            <ContactPanel conversationId={selectedId!} />
          </aside>
        </>
      )}

      {/* Col 3: Contato full-screen (mobile < 768px) — aberto ao tocar no nome
          do cliente no header da conversa. No mobile o painel lateral não cabe;
          este overlay cobre a tela com um botão "Voltar" para a conversa. */}
      {isMobile && selectedId !== null && contactOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Dados do contato"
          className="absolute inset-0 z-30 flex flex-col"
          style={{ background: 'var(--bg-elev-1)' }}
        >
          <div
            className="flex items-center px-4 py-3 flex-shrink-0"
            style={{
              borderBottom: '1px solid var(--border-subtle)',
              background: 'var(--bg-elev-1)',
              boxShadow: 'var(--elev-1)',
            }}
          >
            <button
              type="button"
              onClick={() => setContactOpen(false)}
              className="flex items-center gap-2 font-sans font-medium transition-opacity duration-fast hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-azul rounded-xs"
              style={{ fontSize: 'var(--text-sm)', color: 'var(--brand-azul)' }}
              aria-label="Voltar para a conversa"
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4"
                aria-hidden="true"
              >
                <path d="M10 13L5 8l5-5" />
              </svg>
              Voltar
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ContactPanel conversationId={selectedId} />
          </div>
        </div>
      )}

      {/* F50-S02: aviso "seu histórico não veio" — só para quem é vendedor
          com aparelho WORKING e importação vazia/falha após o worker de
          retomada (F50-S01) esgotar as tentativas. Ver useSyncHistoryAlert. */}
      <SyncHistoryAlertDialog open={syncHistoryAlert.open} onDismiss={syncHistoryAlert.dismiss} />
    </div>
  );
}
